"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import {
  clearGuestCheckout,
  guestEmailSchema,
  readGuestCheckout,
  saveGuestCheckout,
  setClaimTokenCookie,
} from "@/components/offer/guest-checkout-storage";
import { useI18n } from "@/components/i18n-provider";
import { useSupabase, useSupabaseUser } from "@/components/supabase-provider";
import {
  OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS,
  OAUTH_NEXT_COOKIE_NAME,
} from "@/lib/auth/oauth-constants";
import { trackEvent } from "@/lib/analytics";
import { trackPaymentOutcomeEvent } from "@/lib/payments/payment-outcome-analytics";
import { requestQrisReissue } from "@/components/payments/qris-payment-step.helpers";
import { getGa4ClientIdFromCookie } from "@/lib/ga-client-id";
import {
  createPaymentIdempotencyKey,
  PAYMENT_IDEMPOTENCY_KEY_HEADER,
} from "@/lib/payments/payment-idempotency";

interface QrData {
  paymentId: string;
  qrString: string | null;
  invoiceUrl: string | null;
  amount: number;
  claimToken: string;
}

type Step = "idle" | "qr" | "oauth" | "complete";

export function useGuestCheckoutFlow(
  options: {
    open: boolean;
    amount: number;
    onOpenChange: (open: boolean) => void;
  },
) {
  const { locale, dictionary } = useI18n();
  const { supabase } = useSupabase();
  const supabaseUser = useSupabaseUser();
  const copy = dictionary.dashboard.offerPage.bundle;

  const [step, setStep] = useState<Step>("idle");
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [qrData, setQrData] = useState<QrData | null>(null);
  const redirectTimer = useRef<number | null>(null);
  // Tracks whether the QrisPaymentStep has reached a terminal state
  // (expired or failed); used to prevent checkout_abandoned from
  // double-firing after payment_expired or payment_failed.
  const terminalReachedRef = useRef(false);
  const isStartingRef = useRef(false);
  const activeIdempotencyKeyRef = useRef<string | null>(null);

  const clearRedirect = useCallback(() => {
    if (redirectTimer.current !== null) {
      window.clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
  }, []);

  useEffect(() => clearRedirect, [clearRedirect]);

  const reset = useCallback((preserveIdempotencyKey = false) => {
    clearRedirect();
    setIsStarting(false);
    setError(null);
    setEmailError(null);
    setQrData(null);
    setStep("idle");
    terminalReachedRef.current = false;
    if (!isStartingRef.current && !preserveIdempotencyKey) {
      activeIdempotencyKeyRef.current = null;
    }
  }, [clearRedirect]);

  // Restore an in-flight guest checkout persisted to localStorage when the
  // QR was issued, so a tab close or refresh cannot orphan a paid-but-
  // unclaimed purchase: the QR step remounts and its status polling resolves
  // pending, success, or expiry.
  useEffect(() => {
    if (
      !options.open ||
      supabaseUser ||
      step !== "idle" ||
      qrData ||
      isStarting
    ) {
      return;
    }

    const stored = readGuestCheckout();
    if (!stored) {
      return;
    }

    setQrData({
      paymentId: stored.paymentId,
      qrString: stored.qrString,
      invoiceUrl: stored.invoiceUrl,
      amount: stored.amount,
      claimToken: stored.claimToken,
    });
    setStep("qr");
  }, [options.open, supabaseUser, step, qrData, isStarting]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Closing while the QR is on screen is the highest-intent drop-off we
      // have; instrument it so expiry vs failure vs abandonment is measurable.
      // Skip if the QrisPaymentStep already reached a terminal state (expiry
      // or failure was already tracked).
      if (step === "qr" && qrData && !terminalReachedRef.current) {
        trackPaymentOutcomeEvent(
          "checkout_abandoned",
          {
            paymentId: qrData.paymentId,
            tier: "Career OS",
            amount: qrData.amount,
            guest: true,
          },
          { locale, source: "career_booster_lp" },
        );
      }
      reset();
    }
    options.onOpenChange(next);
  };

  const startCheckout = async () => {
    if (isStartingRef.current) {
      return;
    }

    if (supabaseUser) {
      window.location.href = `/${locale}/dashboard/offer`;
      return;
    }

    const parsedEmail = guestEmailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setEmailError(dictionary.emailValidationError);
      return;
    }

    isStartingRef.current = true;
    setIsStarting(true);
    setError(null);
    setEmailError(null);

    try {
      const gaClientId = getGa4ClientIdFromCookie();
      const idempotencyKey =
        activeIdempotencyKeyRef.current ?? createPaymentIdempotencyKey();
      activeIdempotencyKeyRef.current = idempotencyKey;
      const response = await fetch("/api/payment/guest-bundle", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          [PAYMENT_IDEMPOTENCY_KEY_HEADER]: idempotencyKey,
        },
        body: JSON.stringify({
          email: parsedEmail.data,
          ...(gaClientId ? { gaClientId } : {}),
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof result?.error === "string" ? result.error : copy.error,
        );
      }

      const paymentId: string | undefined = result?.paymentId;
      if (!paymentId) {
        throw new Error(copy.error);
      }

      const nextQrData: QrData = {
        paymentId,
        qrString: result?.qrString ?? null,
        invoiceUrl: result?.invoiceUrl ?? null,
        amount: Number(result?.amount ?? options.amount),
        claimToken: result?.claimToken ?? "",
      };

      if (nextQrData.claimToken) {
        setClaimTokenCookie(nextQrData.claimToken);
        saveGuestCheckout({
          paymentId: nextQrData.paymentId,
          claimToken: nextQrData.claimToken,
          amount: nextQrData.amount,
          qrString: nextQrData.qrString,
          invoiceUrl: nextQrData.invoiceUrl,
        });
      }

      setQrData(nextQrData);
      setStep("qr");

      trackEvent("guest_checkout_started", {
        locale,
        source: "career_booster_lp",
        paymentId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.error);
    } finally {
      isStartingRef.current = false;
      setIsStarting(false);
    }
  };

  const handlePaymentSuccess = useCallback(
    (_paymentId: string) => {
      activeIdempotencyKeyRef.current = null;
      trackEvent("guest_payment_completed", {
        locale,
        source: "career_booster_lp",
        paymentId: _paymentId,
      });
      setStep("oauth");
    },
    [locale],
  );

  const handleRetry = useCallback(async () => {
    if (!qrData) {
      reset();
      return;
    }

    try {
      await requestQrisReissue(qrData.paymentId, qrData.claimToken);
      clearGuestCheckout();
      reset(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : copy.error);
    }
  }, [copy.error, qrData, reset]);

  const handleTerminalState = useCallback(() => {
    terminalReachedRef.current = true;
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    if (!qrData?.claimToken) {
      return;
    }

    try {
      setClaimTokenCookie(qrData.claimToken);

      const nextPath = `/${locale}/dashboard/offer`;
      document.cookie = `${OAUTH_NEXT_COOKIE_NAME}=${encodeURIComponent(nextPath)}; path=/; max-age=${OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;

      trackEvent("guest_oauth_start", {
        locale,
        source: "career_booster_lp",
      });

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/oauth?next=${encodeURIComponent(nextPath)}`,
        },
      });

      if (oauthError) {
        throw oauthError;
      }
    } catch (err) {
      console.error("Failed to start OAuth:", err);
      setError(dictionary.oauthStartError);
    }
  }, [qrData?.claimToken, locale, supabase.auth, dictionary]);

  return {
    copy,
    dictionary,
    email,
    emailError,
    error,
    isStarting,
    qrData,
    step,
    handleOpenChange,
    handleGoogleLogin,
    handlePaymentSuccess,
    handleRetry,
    handleTerminalState,
    onEmailChange: (value: string) => {
      setEmail(value);
      if (emailError) {
        setEmailError(null);
      }
    },
    startCheckout,
  };
}
